import { memo, useCallback, useEffect, useMemo, useState } from 'react'

import './index.scss'
import SelectTag from '../SelectTag'
import Input from '../Input'
import Button from '../Button'
import { KEY } from '../../data/variables'
import { InputError } from '../Input/types'

type TagSelect = {
  value: string[] | number[]
  header?: string
  label: string
  size: 'l' | 'm' | 's'
  options: { [key: string]: string }
  text?: string
  onInput: (val: string) => any
  type: 'select' | 'text'
  buttonLabel: string
  error: InputError
}

const TagSelect = memo(({
  value = [], header, label, options = {}, text, onInput = () => {}, type = 'select',
  buttonLabel, error, size = 'l',
}: TagSelect) => {
  const [search, setSearch] = useState<string>('')

  const addValue = useCallback((val: string) => {
    const tempValue = [...value]
    tempValue.push(val)
    onInput(tempValue)
    setSearch('')
  }, [value, onInput])

  useEffect(() => {
    if (search && options[search]) {
      addValue(search)
    }
  }, [search])

  const deleteTag = useCallback((id: string) => {
    const idx = value.indexOf(id)
    if (idx > -1) {
      const tempValue = [...value]
      tempValue.splice(idx, 1)
      onInput(tempValue)
    }
  }, [value, onInput])

  const onKeyPress = useCallback((e: Event) => {
    if (e.key === KEY.ENTER) {
      addValue(search)
    }
  }, [addValue])

  const filteredOptions = useMemo(() => {
    const valueSet = new Set(value)
    return Object.keys(options).reduce((obj, key) => {
      if (!valueSet.has(Number(key)) && !valueSet.has(key.toString())) obj[key] = options[key]
      return obj
    }, {})
  }, [options, value])

  const onBlur = useCallback(() => {
    if (type === 'text' && search) {
      addValue(search)
    }
  }, [type, addValue, search])

  return (
    <div className="TagSelect">
      {value?.length > 0 && <h3>{header}</h3>}
      <div className="TagSelect__tags">
        {value && value?.map((key) => {
          const tagLabel = options[key] ?? key
          return <SelectTag id={key} label={tagLabel} onDelete={() => deleteTag(key)} />
        })}
      </div>
      <Input
        type={type}
        label={label}
        size={size}
        searchable={true}
        value={search}
        text={text}
        options={filteredOptions}
        onInput={(val: string) => setSearch(val)}
        onKeyPress={(e) => onKeyPress(e)}
        error={error}
        onBlur={onBlur}
      />
      {type === 'text' && (
        <Button
          label={buttonLabel}
          size="s"
          type="secondary"
          onClick={() => addValue(search)}
        />
      )}
    </div>
  )
})

export default TagSelect
